import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { HiOutlineBell, HiOutlineSparkles, HiOutlineCheck, HiOutlineTrash, HiOutlineUserAdd } from 'react-icons/hi';
import { FiRefreshCw, FiCheckCircle, FiMessageSquare } from 'react-icons/fi';
import {
  getNotifications, getUnreadCount,
  markNotificationRead, markAllNotificationsRead, deleteNotification,
} from '../services/api';

const POLL_INTERVAL = 30000; // 30 secondes

const TYPE_ICONS = {
  ai_generation: HiOutlineSparkles,
  squash_sync: FiRefreshCw,
  assignment: HiOutlineUserAdd,
  status_update: FiCheckCircle,
  comment: FiMessageSquare,
};

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const now = new Date();
  const date = new Date(dateStr);
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return 'A l\'instant';
  if (diff < 3600) return `Il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `Il y a ${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `Il y a ${Math.floor(diff / 86400)}j`;
  return date.toLocaleDateString('fr-FR');
}

export default function NotificationBell() {
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);
  const bellRef = useRef(null);
  const navigate = useNavigate();

  const isDark = document.documentElement.getAttribute('data-theme') === 'dark';

  const fetchUnreadCount = useCallback(async () => {
    try {
      const { data } = await getUnreadCount();
      setUnreadCount(data.count || 0);
    } catch {
      // silencieux
    }
  }, []);

  // Polling du compteur non-lu
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Charger les notifications quand on ouvre le panneau
  const handleToggle = async () => {
    const willOpen = !open;
    setOpen(willOpen);
    if (willOpen) {
      setLoading(true);
      try {
        const { data } = await getNotifications();
        setNotifications(data);
      } catch {
        // silencieux
      } finally {
        setLoading(false);
      }
    }
  };

  // Fermer au clic exterieur
  useEffect(() => {
    function handleClickOutside(e) {
      if (
        open &&
        panelRef.current && !panelRef.current.contains(e.target) &&
        bellRef.current && !bellRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // Positionner le panneau par rapport a la cloche
  const [panelPos, setPanelPos] = useState({ top: 0, left: 0 });
  useEffect(() => {
    if (open && bellRef.current) {
      const rect = bellRef.current.getBoundingClientRect();
      setPanelPos({
        top: rect.bottom + 8,
        left: rect.left,
      });
    }
  }, [open]);

  const handleMarkRead = async (notif) => {
    if (notif.is_read) return;
    try {
      await markNotificationRead(notif.id);
      setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {
      // silencieux
    }
  };

  const handleClick = async (notif) => {
    await handleMarkRead(notif);
    if (notif.project_id) {
      navigate(`/projects/${notif.project_id}`);
      setOpen(false);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch {
      // silencieux
    }
  };

  const handleDelete = async (e, notifId) => {
    e.stopPropagation();
    try {
      await deleteNotification(notifId);
      const removed = notifications.find(n => n.id === notifId);
      setNotifications(prev => prev.filter(n => n.id !== notifId));
      if (removed && !removed.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch {
      // silencieux
    }
  };

  return (
    <div className="notification-bell-wrapper">
      <button
        ref={bellRef}
        className="notification-bell-btn"
        onClick={handleToggle}
        title="Notifications"
      >
        <HiOutlineBell size={20} />
        {unreadCount > 0 && (
          <span className="notification-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={panelRef}
          className="notification-panel"
          style={{
            position: 'fixed',
            top: panelPos.top,
            left: panelPos.left,
            width: 340,
            maxHeight: 440,
            background: isDark ? '#1c2333' : '#fff',
            border: `1px solid ${isDark ? '#3d4250' : '#e2e8f0'}`,
            borderRadius: 8,
            boxShadow: '0 10px 25px rgba(0,0,0,0.25)',
            zIndex: 1050,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            borderBottom: `1px solid ${isDark ? '#3d4250' : '#e2e8f0'}`,
          }}>
            <span style={{ fontWeight: 600, fontSize: 14, color: isDark ? '#f3f4f6' : '#1a202c' }}>
              Notifications
            </span>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                style={{
                  background: 'none', border: 'none',
                  color: '#2ecc71', fontSize: 12, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 4,
                  padding: '4px 8px', borderRadius: 4,
                }}
              >
                <HiOutlineCheck size={14} />
                Tout marquer lu
              </button>
            )}
          </div>

          <div style={{ overflowY: 'auto', flex: 1, maxHeight: 380 }}>
            {loading && (
              <div style={{ padding: '32px 16px', textAlign: 'center', color: isDark ? '#9ca3af' : '#6b7280', fontSize: 13 }}>
                Chargement...
              </div>
            )}
            {!loading && notifications.length === 0 && (
              <div style={{ padding: '32px 16px', textAlign: 'center', color: isDark ? '#9ca3af' : '#6b7280', fontSize: 13 }}>
                Aucune notification
              </div>
            )}
            {!loading && notifications.map(notif => {
              const IconComp = TYPE_ICONS[notif.type] || HiOutlineBell;
              const isUnread = !notif.is_read;
              return (
                <button
                  type="button"
                  key={notif.id}
                  onClick={() => handleClick(notif)}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    padding: '10px 16px',
                    cursor: 'pointer',
                    border: 'none',
                    borderBottom: `1px solid ${isDark ? '#2d3139' : '#f1f3f8'}`,
                    borderLeft: isUnread ? '3px solid #2ecc71' : '3px solid transparent',
                    background: isUnread
                      ? (isDark ? 'rgba(46,204,113,0.08)' : 'rgba(46,204,113,0.04)')
                      : 'transparent',
                    opacity: notif.is_read ? 0.7 : 1,
                    font: 'inherit',
                    color: 'inherit',
                    textAlign: 'left',
                    width: '100%',
                  }}
                >
                  <div style={{
                    flexShrink: 0, width: 28, height: 28,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    borderRadius: '50%',
                    background: isUnread
                      ? (isDark ? 'rgba(46,204,113,0.15)' : 'rgba(46,204,113,0.1)')
                      : (isDark ? '#2d3139' : '#f1f3f8'),
                    color: isUnread ? '#2ecc71' : (isDark ? '#9ca3af' : '#6b7280'),
                    marginTop: 2,
                  }}>
                    <IconComp size={16} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: isDark ? '#f3f4f6' : '#1a202c', marginBottom: 2 }}>
                      {notif.title}
                    </div>
                    <div style={{
                      fontSize: 12, color: isDark ? '#9ca3af' : '#6b7280',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      {notif.message}
                    </div>
                    <div style={{ fontSize: 11, color: isDark ? '#6b7280' : '#9ca3af', marginTop: 3 }}>
                      {timeAgo(notif.created_at)}
                    </div>
                  </div>
                  <button
                    onClick={(e) => handleDelete(e, notif.id)}
                    title="Supprimer"
                    style={{
                      background: 'none', border: 'none',
                      color: isDark ? '#6b7280' : '#d1d5db',
                      cursor: 'pointer', padding: 4, borderRadius: 4, flexShrink: 0,
                    }}
                  >
                    <HiOutlineTrash size={14} />
                  </button>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
